import { useEffect, useMemo, useState } from "react";
import { adsConfig } from "../analytics/config";
import { getConsentSnapshot, onCookieConsentChange } from "../utils/cookieConsent";
import { getCurrentPathname, onNavigationChange } from "../utils/navigationEvents";

const SESSION_HIDE_KEY = "xphub_ads_hidden_until_refresh";

const BASE_CONTAINER_STYLE = {
  position: "fixed",
  right: 16,
  bottom: 16,
  width: "min(320px, calc(100vw - 24px))",
  zIndex: 60,
  border: "1px solid rgba(255,255,255,0.14)",
  borderRadius: 12,
  background: "rgba(11, 16, 26, 0.92)",
  boxShadow: "0 10px 30px rgba(0,0,0,0.35)",
  overflow: "hidden",
  color: "#d4d9e2",
  fontFamily: "Inter, system-ui, sans-serif",
};

const HEADER_STYLE = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "8px 12px",
  fontSize: 12,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  borderBottom: "1px solid rgba(255,255,255,0.08)",
  background: "rgba(255,255,255,0.02)",
};

const SLOT_STYLE = {
  padding: "10px 12px 12px",
  minHeight: 88,
  display: "grid",
  gap: 8,
  alignContent: "center",
};

const SLOT_MAP = [
  {
    id: "dashboard_inline",
    label: "Dashboard companion",
    flag: "REACT_APP_AD_SLOT_DASHBOARD",
    enabled: adsConfig.placements.dashboard,
    match: (pathname) => pathname === "/dashboard",
  },
  {
    id: "game_detail_footer",
    label: "Game detail companion",
    flag: "REACT_APP_AD_SLOT_GAME_DETAIL",
    enabled: adsConfig.placements.gameDetail,
    match: (pathname) => /^\/game\/\d+/.test(pathname),
  },
  {
    id: "news_feed_inline",
    label: "News feed companion",
    flag: "REACT_APP_AD_SLOT_NEWS",
    enabled: adsConfig.placements.news,
    match: (pathname) => pathname.startsWith("/news"),
  },
];

function shouldShowByConsent(consent) {
  return Boolean(consent?.ads);
}

function readHiddenFlag() {
  if (typeof window === "undefined") return false;
  try {
    return sessionStorage.getItem(SESSION_HIDE_KEY) === "1";
  } catch {
    return false;
  }
}

function writeHiddenFlag(hidden) {
  if (typeof window === "undefined") return;
  try {
    if (hidden) sessionStorage.setItem(SESSION_HIDE_KEY, "1");
    else sessionStorage.removeItem(SESSION_HIDE_KEY);
  } catch {
    // noop
  }
}

function resolvePlacement(pathname) {
  return SLOT_MAP.find((slot) => slot.enabled && slot.match(pathname)) || null;
}

function AdPlacementsRail() {
  const [pathname, setPathname] = useState(() => getCurrentPathname());
  const [consent, setConsent] = useState(() => getConsentSnapshot());
  const [hidden, setHidden] = useState(() => readHiddenFlag());

  useEffect(() => onNavigationChange((nextPath) => setPathname(nextPath)), []);

  useEffect(() => {
    setConsent(getConsentSnapshot());
    return onCookieConsentChange((nextConsent) => {
      setConsent({
        ...getConsentSnapshot(),
        ...(nextConsent || {}),
      });
    });
  }, []);

  useEffect(() => {
    writeHiddenFlag(hidden);
  }, [hidden]);

  const placement = useMemo(() => resolvePlacement(pathname), [pathname]);

  if (!adsConfig.enabled || hidden || !shouldShowByConsent(consent) || !placement) {
    return null;
  }

  return (
    <aside aria-label="Espacio publicitario opcional" style={BASE_CONTAINER_STYLE}>
      <div style={HEADER_STYLE}>
        <strong style={{ fontWeight: 700 }}>Publicidad</strong>
        <button
          type="button"
          onClick={() => setHidden(true)}
          style={{
            border: "none",
            background: "transparent",
            color: "#a8b1c1",
            cursor: "pointer",
            fontSize: 16,
            lineHeight: 1,
          }}
          aria-label="Ocultar publicidad por esta sesión"
        >
          ×
        </button>
      </div>

      <div style={SLOT_STYLE}>
        <div style={{ fontSize: 14, fontWeight: 600 }}>{placement.label}</div>
        <div style={{ fontSize: 12, color: "#99a4b8" }}>Slot: {placement.id}</div>
        <div style={{ fontSize: 11, color: "#768199" }}>Flag: {placement.flag}</div>
      </div>
    </aside>
  );
}

export default AdPlacementsRail;
